<script setup lang="ts">
type Props = {
  onTop?: boolean;
  showText?: boolean;
  withoutBg?: boolean;
};

const {
  onTop = false,
  showText = true,
  withoutBg = false,
} = defineProps<Props>();

const { showMoneyFormat } = useWallet();

const {
  isEmailVerified,
  isPhoneVerifiedOrComplete,
  isDocumentComplete,
  isAddressComplete,
  isVerifiedToWithdraw,
} = useUserValidations();

const { getValidationModule, getMinValueToTriggerModule } =
  useModulesValidation();
</script>

<template>
  <div
    v-if="!isVerifiedToWithdraw"
    :class="[
      $style.alertWithdraw,
      { [$style.onTop]: onTop },
      { [$style.withoutBg]: withoutBg },
    ]"
  >
    <div :class="$style.alertText">
      <p
        v-if="showText"
        v-html="
          $t('account.user_data.complete_registration', {
            draw_amount: showMoneyFormat({
              value: getMinValueToTriggerModule('first_withdraw') / 100,
            }),
          })
        "
      />

      <ul :class="$style.alertList">
        <li
          v-for="validate in getValidationModule('first_withdraw')"
          :key="validate.name"
        >
          <template v-if="validate.name === 'email'">
            <NuxtIcon
              v-if="isEmailVerified"
              :class="[$style.alertIcon, $style.success]"
              name="check"
            />
            <NuxtIcon
              v-else
              :class="[$style.alertIcon, $style.error]"
              name="close"
            />
          </template>
          <template v-if="validate.name === 'phone'">
            <NuxtIcon
              v-if="isPhoneVerifiedOrComplete"
              :class="[$style.alertIcon, $style.success]"
              name="check"
            />
            <NuxtIcon
              v-else
              :class="[$style.alertIcon, $style.error]"
              name="close"
            />
          </template>
          <template v-if="validate.name === 'address'">
            <NuxtIcon
              v-if="isAddressComplete"
              :class="[$style.alertIcon, $style.success]"
              name="check"
            />
            <NuxtIcon
              v-else
              :class="[$style.alertIcon, $style.error]"
              name="close"
            />
          </template>
          <template v-if="validate.name === 'kyc'">
            <NuxtIcon
              v-if="isDocumentComplete"
              :class="[$style.alertIcon, $style.success]"
              name="check"
            />
            <NuxtIcon
              v-else
              :class="[$style.alertIcon, $style.error]"
              name="close"
            />
          </template>
          {{ $t(`general.${validate.name}`) }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style module lang="postcss">
.alertWithdraw {
  @apply flex items-center justify-center sm:justify-start gap-2 bg-warning/10 text-warning rounded-lg py-3 sm:py-4 px-4 sm:px-5 mt-4 sm:mt-6 text-center;

  &.onTop {
    @apply mb-4 sm:mb-6 mt-0;
  }

  .alertText {
    @apply flex flex-col gap-2;

    p {
      @apply text-xs sm:text-sm font-medium;
    }
  }

  .alertList {
    @apply flex flex-row justify-center sm:justify-start items-center gap-3 sm:gap-4;

    li {
      @apply flex flex-col xs:flex-row items-center justify-center gap-1 xs:gap-1.5 sm:gap-2 font-semibold text-xs sm:text-sm whitespace-nowrap;

      .alertIcon {
        @apply text-[.5rem] sm:text-[.62rem] w-3 sm:w-3.5 h-3 sm:h-3.5 rounded-full text-white bg-black flex items-center justify-center;

        &.success {
          @apply bg-success;
        }

        &.error {
          @apply bg-error;
        }
      }
    }
  }

  &.withoutBg {
    @apply bg-transparent p-0 mb-3;

    .alertList {
      @apply flex-wrap justify-center gap-y-1;
    }
  }
}
</style>
